import { FetchStatus, QueryStatus } from "@tanstack/query-core/src/types";
import {
  WSButton,
  WSCentered,
  WSContainer,
  WSElement,
  WSLoader,
  WSText
} from "@wingspanhq/fe-component-library";
import { AxiosError } from "axios";
import React, { ReactNode, useEffect, useMemo, useRef } from "react";
import { EmptySpaceErrorMessage } from "../shared/components/EmptyStateErrorMessage";
import { EmptyStateForbidden } from "../shared/components/EmptyStateForbidden";
import { EmptyStateNotFound } from "../shared/components/EmptyStateNotFound";
import { IS_DEV_ENV } from "../shared/constants/environment";
import { openIntercom } from "../shared/utils/intercom";

type Refetch = () => Promise<unknown>;

export type WSQueryRenderErrorMessage = (
  errors: any,
  refetch: Refetch
) => React.ReactNode;

const renderDefaultErrorMessage: WSQueryRenderErrorMessage = (
  error,
  refetch
) => {
  switch ((error as AxiosError)?.response?.status) {
    case 403:
      return <EmptyStateForbidden />;
    case 404:
      return <EmptyStateNotFound />;
    default:
      return <EmptySpaceErrorMessage onRetry={refetch} />;
  }
};

export type WSQueryRenderErrorsMessage = (
  errors: any[],
  refetch: Refetch
) => React.ReactNode;

const renderDefaultErrorsMessage: WSQueryRenderErrorsMessage = (
  errors,
  refetch
) => {
  return Array.isArray(errors) && errors.length > 0
    ? renderDefaultErrorMessage(errors[0], refetch)
    : null;
};

type ErrorBoundaryProps = {};

type ErrorBoundaryState = {
  hasError: boolean;
  error?: Error;
  errors?: Error[];
};

export class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, errors: [error] };
  }

  componentDidCatch(error: Error, info: { componentStack: string }) {
    console.error(error, info)
  }

  render() {
    if (this.state.hasError) {
      return (
        <WSContainer
          verticalPadding
          style={{ height: "100%", display: "flex" }}
          data-testid="serviceError"
        >
          <WSCentered span={{ xs: "8", m: "6" }}>
            <WSText.Heading4>Uh-oh! Error! 🤕</WSText.Heading4>
            <WSText mt="XL">
              Sorry about that, it appears we've hit a snag. Please reload your
              browser and try again, or{" "}
              <WSButton.Link onClick={openIntercom}>
                contact support
              </WSButton.Link>{" "}
              if the problem persists.
            </WSText>
            {IS_DEV_ENV && (
              <WSText color="red400" mt="M">
                One or more errors are occurring in this component
                ErrorBoundary:
              </WSText>
            )}
            {IS_DEV_ENV &&
              this.state.errors &&
              (this.state.errors as any[]).map((err) => {
                console.log("ErrorBoundary", err);
                return (
                  <WSText color="red400" mt="M">
                    {err.message}
                  </WSText>
                );
              })}
          </WSCentered>
        </WSContainer>
      );
    }

    return <>{this.props.children}</>;
  }
}

export const defaultRenderLoader = () => (
  <WSElement my="5XL">
    <WSLoader.Spinner />
  </WSElement>
);

type Query = {
  data: any;
  isLoading: boolean;
  isError: boolean;
  error: any;
  refetch: Refetch;
  isSuccess: boolean;
  status: QueryStatus
  fetchStatus: FetchStatus
  dataUpdatedAt: number;
  errorUpdatedAt: number
  failureCount: number
  failureReason: any
  errorUpdateCount: number
  isFetched: boolean
  isFetchedAfterMount: boolean
  isFetching: boolean
  isLoadingError: boolean
  isInitialLoading: boolean
  isPaused: boolean
  isPlaceholderData: boolean
  isPreviousData: boolean
  isRefetchError: boolean
  isRefetching: boolean
  isStale: boolean
  remove: () => void
};

export type ChildQuery<T> = T extends { data: infer U }
  ? T & { data: NonNullable<U> }
  : never;

export type WSQueryRenderLoader = () => React.ReactNode;

type PropsQuery<Q extends Query> = {
  query: Q;
  renderLoader?: WSQueryRenderLoader;
  renderError?: WSQueryRenderErrorMessage;
  /**
   * @deprecated This property is deprecated. Let's use hooks instead.
   */
  onSuccess?: (props: ChildQuery<Q>) => void;
} & (
  | {
  children(props: ChildQuery<Q>): React.ReactNode;
  renderChildren?: false;
}
  | {
  children: ReactNode;
  renderChildren: true;
}
  );

const QueryComponent = <Q extends Query = Query>({
                                                   children,
                                                   renderError = renderDefaultErrorMessage,
                                                   renderLoader = defaultRenderLoader,
                                                   onSuccess,
                                                   query,
                                                   renderChildren
                                                 }: PropsQuery<Q>): any => {
  const isSuccess = query.isSuccess;
  const successRef = useRef(false);

  useEffect(() => {
    if (!successRef.current && isSuccess) {
      successRef.current = true;
      onSuccess?.(query as any);
    }
  }, [isSuccess, onSuccess, query]);


  if (query.isSuccess) {
    return !renderChildren && typeof children === "function"
      ? children(query as any)
      : children;
  }

  if (query.isError) {
    return renderError(query.error, query.refetch);
  }

  return renderLoader();
};

type ChildQueries<T> = {
  [K in keyof T]: T[K] extends { data: infer U }
    ? T[K] & { data: NonNullable<U> }
    : never;
};

type ChildQueriesData<T> =
  {
    [K in keyof T as `${string & K}Data`]: T[K] extends { data: infer U } ? NonNullable<U> : never;
  };

type ReactFC = React.FC<any> | React.LazyExoticComponent<React.FC<any>>;

type ChildProps<Q> = ChildQueriesData<Q> & ChildQueries<Q>;

type Queries = { [key: string]: Query };

type PropsQueriesFuncAsChild<Q extends Queries> = {
  queries: Q;
  renderLoader?: WSQueryRenderLoader;
  renderErrors?: WSQueryRenderErrorsMessage;
  onSuccess?(props: ChildQueries<Q>): void;
  onError?(props: ChildQueries<Q>): void;
  children(props: ChildProps<Q>): React.ReactNode;
};

type ChildComponentProps<P extends ReactFC, Q extends Queries> = Omit<
  React.ComponentProps<P>,
  keyof ChildProps<Q> | "children"
>;

type PropsQueriesCompAsProp<
  Q extends Queries,
  P extends React.FC<ChildProps<Q>>
> = ChildComponentProps<P, Q> extends Record<string, never>
  ? {
    queries: Q;
    renderLoader?: WSQueryRenderLoader;
    renderErrors?: WSQueryRenderErrorsMessage;
    /**
     * @deprecated This property is deprecated. Let's use hooks instead.
     */
    onSuccess?(props: ChildQueries<Q>): void;
    /**
     * @deprecated This property is deprecated. Let's use hooks instead.
     */
    onError?(props: ChildQueries<Q>): void;
    children?: never;
    component: P;
    componentProps?: Record<string, never>;
  }
  : {
    queries: Q;
    renderLoader?: WSQueryRenderLoader;
    renderErrors?: WSQueryRenderErrorsMessage;
    /**
     * @deprecated This property is deprecated. Let's use hooks instead.
     */
    onSuccess?(props: ChildProps<Q>): void;
    /**
     * @deprecated This property is deprecated. Let's use hooks instead.
     */
    onError?(props: ChildProps<Q>): void;
    children?: never;
    component: P;
    componentProps: ChildComponentProps<P, Q>;
  };

const QueriesComponent = <Q extends Queries, P extends ReactFC>({
                                                                  children,
                                                                  renderErrors = renderDefaultErrorsMessage,
                                                                  renderLoader = defaultRenderLoader,
                                                                  onSuccess,
                                                                  onError,
                                                                  queries,
                                                                  component: Component,
                                                                  componentProps = {} as ChildComponentProps<P, Q>
                                                                }: P extends ReactFC
  ? PropsQueriesCompAsProp<Q, P>
  : PropsQueriesFuncAsChild<Q>): any => {
  const queriesHash = Object.values(queries).map(q => [q.dataUpdatedAt, q.status, q.fetchStatus].join("-")).join("--");

  const isSuccess = Object.values(queries).every((value) => value.isSuccess);
  const successRef = useRef(false);

  useEffect(() => {
    if (!successRef.current && isSuccess) {
      successRef.current = true;
      onSuccess?.(queries as any);
    }
  }, [isSuccess, onSuccess, queries, queriesHash]);

  const childParams = useMemo(() => {
    const queriesData = Object.keys(queries).reduce((acc, key) => {
      acc[`${key}Data`] = queries[key].data;
      return acc;
    }, {} as any);

    return { ...componentProps, ...queries, ...queriesData };
  }, [queries, queriesHash, componentProps]);

  if (
    Object.values(queries).every((value) => value.isSuccess)
  ) {
    return typeof children === "function" ? (
      (children as any)(childParams)
    ) : Component ? (
      <Component {...childParams} />
    ) : null;
  }

  if (Object.values(queries).some((value) => value.isError)) {
    const failedQueries = Object.values(queries).filter((v) => v.isError);
    const errors = failedQueries.map((value) => value.error);
    onError?.(queries as any);
    return renderErrors(errors, () =>
      Promise.all(Object.values(failedQueries).map((q) => q.refetch()))
    );
  }

  return renderLoader();
};

export function WSQuery<Q extends Query = Query>(
  props: PropsQuery<Q>
): React.FunctionComponentElement<{}> {
  return (
    <ErrorBoundary>
      <QueryComponent {...props} />
    </ErrorBoundary>
  );
}

export function WSQueries<
  Q extends Queries = Queries,
  P extends ReactFC = React.FC<unknown>
>(
  props: PropsQueriesFuncAsChild<Q> | PropsQueriesCompAsProp<Q, P>
): React.FunctionComponentElement<{}> {
  return (
    <ErrorBoundary>
      <QueriesComponent {...(props as any)} />
    </ErrorBoundary>
  );
}
